import { useState, useCallback, useEffect } from 'react';
import { Globe, Copy, Check, Terminal, RefreshCw } from 'lucide-react';
import type { ToolComponentProps } from './index';

interface IPResult {
  ipv4: string | null;
  ipv6: string | null;
  timestamp: string;
  source?: { ipv4?: string; ipv6?: string };
}

// IPv4 services (in order of preference)
const IPV4_SERVICES = [
  { url: 'https://api.ipify.org?format=json', extract: (d: { ip: string }) => d.ip, name: 'ipify' },
  { url: 'https://api.my-ip.io/v2/ip.json', extract: (d: { ip: string }) => d.ip, name: 'my-ip.io' },
  { url: 'https://ipv4.icanhazip.com', extract: (d: string) => d.trim(), name: 'icanhazip', isText: true },
];

// IPv6 services (in order of preference)  
const IPV6_SERVICES = [
  { url: 'https://api6.ipify.org?format=json', extract: (d: { ip: string }) => d.ip, name: 'ipify' },
  { url: 'https://ipv6.icanhazip.com', extract: (d: string) => d.trim(), name: 'icanhazip', isText: true },
];

// Try services in order until one succeeds
async function fetchWithFallback(
  services: typeof IPV4_SERVICES,
  timeout = 5000
): Promise<{ ip: string | null; source: string }> {
  for (const service of services) {
    try {
      const response = await fetch(service.url, { 
        signal: AbortSignal.timeout(timeout),
        mode: 'cors',
      });
      
      if (!response.ok) continue;
      
      const data = service.isText ? await response.text() : await response.json();
      const ip = service.extract(data);
      
      if (ip && typeof ip === 'string' && ip.length > 0) {
        return { ip, source: service.name };
      }
    } catch {
      // Try next service
      continue;
    }
  }
  return { ip: null, source: '' };
}

export function MyIPChecker(_props: ToolComponentProps) {
  const [result, setResult] = useState<IPResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [copiedIP, setCopiedIP] = useState<string | null>(null);
  const [copiedCmd, setCopiedCmd] = useState<string | null>(null);

  const handleCheck = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Fetch both IPv4 and IPv6 in parallel with fallbacks
      const [ipv4Result, ipv6Result] = await Promise.all([
        fetchWithFallback(IPV4_SERVICES),
        fetchWithFallback(IPV6_SERVICES),
      ]);

      const ipv4 = ipv4Result.ip;
      const ipv6Raw = ipv6Result.ip;
      
      // Validate IPv6 - must contain colon and not be same as IPv4
      const ipv6 = ipv6Raw && ipv6Raw.includes(':') && ipv6Raw !== ipv4 ? ipv6Raw : null;

      if (!ipv4 && !ipv6) {
        setError('Failed to detect IP addresses. Please try again.');
        return;
      }

      setResult({
        ipv4,
        ipv6,
        timestamp: new Date().toISOString(),
        source: {
          ipv4: ipv4Result.source,
          ipv6: ipv6Result.source,
        },
      });
    } catch {
      setError('Failed to detect IP addresses. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Auto-fetch IP on mount
  useEffect(() => {
    handleCheck();
  }, [handleCheck]);

  const copyToClipboard = useCallback(async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'ipv4' || type === 'ipv6') {
        setCopiedIP(type);
        setTimeout(() => setCopiedIP(null), 2000);
      } else {
        setCopiedCmd(type);
        setTimeout(() => setCopiedCmd(null), 2000);
      }
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, []);

  // Get the base URL for API - in production this would be the actual domain
  const getApiUrl = () => {
    if (typeof window !== 'undefined') {
      return window.location.origin;
    }
    return 'https://secopstools.org';
  };

  const cliCommands = [
    {
      name: 'curl (JSON)',
      command: `curl -s ${getApiUrl()}/api/myip | jq -r '.ip'`,
      description: 'Get your IP in JSON format and extract with jq',
    },
    {
      name: 'curl (Plain)',
      command: `curl -s ${getApiUrl()}/api/myip/plain`,
      description: 'Get your IP as plain text (no parsing required)',
    },
    {
      name: 'wget',
      command: `wget -qO- ${getApiUrl()}/api/myip/plain`,
      description: 'Get your IP using wget',
    },
  ];

  const IPDisplay = ({ label, ip, type }: { label: string; ip: string; type: string }) => (
    <div className="flex flex-col items-center p-4 bg-[var(--bg-secondary)] rounded-lg border border-[var(--border-color)]">
      <div className="text-sm text-[var(--text-muted)] mb-2">{label}</div>
      <div className="flex items-center gap-2 justify-center w-full">
        <span className={`font-mono font-bold text-[var(--accent)] text-center text-base sm:text-xl md:text-2xl ${type === 'ipv6' ? 'break-all' : ''}`}>
          {ip}
        </span>
        <button
          onClick={() => copyToClipboard(ip, type)}
          className="p-2 rounded-lg hover:bg-[var(--bg-tertiary)] transition-colors flex-shrink-0"
          title={`Copy ${label}`}
        >
          {copiedIP === type ? (
            <Check className="w-4 h-4 text-[var(--accent)]" />
          ) : (
            <Copy className="w-4 h-4 text-[var(--text-muted)]" />
          )}
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* IP Display */}
      <div className="flex flex-col items-center justify-center py-6">
        {isLoading ? (
          <div className="flex items-center gap-3 text-[var(--text-muted)]">
            <RefreshCw className="w-6 h-6 animate-spin" />
            <span>Detecting your IP addresses...</span>
          </div>
        ) : error ? (
          <div className="text-center">
            <div className="text-red-400 mb-4">{error}</div>
            <button
              onClick={handleCheck}
              className="btn-primary flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
          </div>
        ) : result ? (
          <div className="w-full max-w-2xl">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Globe className="w-6 h-6 text-[var(--accent)]" />
              <span className="text-lg font-semibold">Your Public IP Addresses</span>
            </div>
            
            <div className="space-y-4">
              {result.ipv4 && (
                <IPDisplay label="IPv4 Address" ip={result.ipv4} type="ipv4" />
              )}
              {result.ipv6 && (
                <IPDisplay label="IPv6 Address" ip={result.ipv6} type="ipv6" />
              )}
              {!result.ipv6 && result.ipv4 && (
                <div className="flex flex-col items-center justify-center p-4 bg-[var(--bg-secondary)] rounded-lg border border-[var(--border-color)]">
                  <div className="text-sm text-[var(--text-muted)] mb-2">IPv6 Address</div>
                  <span className="text-sm text-[var(--text-muted)]">Not available</span>
                </div>
              )}
            </div>
            
            <div className="text-center mt-4">
              <div className="text-xs text-[var(--text-muted)]">
                Checked at {new Date(result.timestamp).toLocaleString()}
              </div>
              <button
                onClick={handleCheck}
                className="mt-2 text-sm text-[var(--accent)] hover:underline flex items-center gap-1 mx-auto"
              >
                <RefreshCw className="w-3 h-3" />
                Refresh
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {/* CLI Commands Section */}
      <div className="border-t border-[var(--border-color)] pt-6">
        <div className="flex items-center gap-2 mb-4">
          <Terminal className="w-5 h-5 text-[var(--accent)]" />
          <h3 className="font-semibold">Command Line Usage</h3>
        </div>
        <p className="text-sm text-[var(--text-muted)] mb-4">
          Use these commands to check your IP from Linux, macOS, or any command line:
        </p>
        
        <div className="space-y-3">
          {cliCommands.map((cmd) => (
            <div
              key={cmd.name}
              className="bg-[var(--bg-secondary)] rounded-lg border border-[var(--border-color)] overflow-hidden"
            >
              <div className="flex items-center justify-between px-4 py-2 bg-[var(--bg-tertiary)] border-b border-[var(--border-color)]">
                <span className="text-sm font-medium">{cmd.name}</span>
                <button
                  onClick={() => copyToClipboard(cmd.command, cmd.name)}
                  className="flex items-center gap-1 text-xs text-[var(--text-muted)] hover:text-[var(--accent)] transition-colors"
                >
                  {copiedCmd === cmd.name ? (
                    <>
                      <Check className="w-3 h-3" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-3 h-3" />
                      Copy
                    </>
                  )}
                </button>
              </div>
              <div className="p-4">
                <code className="text-sm font-mono text-[var(--accent)] break-all">
                  {cmd.command}
                </code>
                <p className="text-xs text-[var(--text-muted)] mt-2">{cmd.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* API Documentation */}
        <div className="mt-6 p-4 bg-[var(--bg-secondary)] rounded-lg border border-[var(--border-color)]">
          <h4 className="font-medium mb-2">API Endpoints</h4>
          <div className="space-y-2 text-sm">
            <div>
              <code className="text-[var(--accent)]">GET /api/myip</code>
              <span className="text-[var(--text-muted)] ml-2">→ Returns JSON: {"{"}"ip": "x.x.x.x", "timestamp": "..."{"}"}</span>
            </div>
            <div>
              <code className="text-[var(--accent)]">GET /api/myip/plain</code>
              <span className="text-[var(--text-muted)] ml-2">→ Returns plain text IP only</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
